import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Loader2, ShieldCheck, TrendingUp, Target,
  FileText, Repeat, Package, Boxes, Award, Building2, CalendarClock,
} from "lucide-react";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;
const money0 = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(/0$/, "")}%`);

const IMG = {
  boardroom: "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  warehouse: "https://images.unsplash.com/photo-1627309366653-2dedc084cdf1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  hospital: "https://images.unsplash.com/photo-1626315869436-d6781ba69d6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  leaders: "https://images.unsplash.com/photo-1573497701240-345a300b8d36?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const LINE_ICONS = {
  "Filmop USA": Boxes,
  "Sol Millennium": Package,
  "American Surgical": ShieldCheck,
  "Brewer": Building2,
  "Vive Health": TrendingUp,
  "Sterile Mate Private Label (Intco)": Award,
  "TechInquest": FileText,
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Overline = ({ children }) => (
  <p className="text-[11px] font-black uppercase tracking-[0.25em] text-cyan-600 mb-4">{children}</p>
);

function EnrollForm({ program }) {
  const [form, setForm] = useState({
    hospital_name: "", contact_name: "", contact_email: "", contact_phone: "",
    estimated_quarterly_spend: "", payout_preference: "credit",
  });
  const [ack, setAck] = useState(false);
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const field = "w-full rounded-lg bg-white border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/25 focus:outline-none text-slate-900 text-sm px-4 py-3 placeholder:text-slate-400 transition-all";
  const label = "block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2";

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending"); setMsg("");
    try {
      const r = await fetch(`${API}/api/rebates/enroll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          estimated_quarterly_spend: form.estimated_quarterly_spend
            ? Number(form.estimated_quarterly_spend) : null,
          safe_harbor_acknowledged: ack,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || "Enrollment failed");
      setStatus("success");
    } catch (err) {
      setStatus("error"); setMsg(err.message);
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 p-10 text-center"
        data-testid="rebate-enroll-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-xl font-black text-slate-900 mb-2">Enrollment received.</h3>
        <p className="text-slate-600 text-sm max-w-md mx-auto leading-relaxed">
          We will counter-sign the one-page agreement and activate your account ahead of the
          January 1, 2027 launch. Accrual starts with your first qualified purchase on or after
          that date, and your first monthly statement arrives February 3, 2027.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit}
      className="rounded-2xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 p-7 lg:p-9 space-y-4"
      data-testid="rebate-enroll-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="rb-hospital" className={label}>Hospital or system *</label>
          <input id="rb-hospital" name="hospital_name" required value={form.hospital_name} onChange={onChange}
            className={field} placeholder="Memorial Health — Supply Chain" data-testid="rebate-form-hospital" />
        </div>
        <div>
          <label htmlFor="rb-name" className={label}>Your name *</label>
          <input id="rb-name" name="contact_name" required value={form.contact_name} onChange={onChange}
            className={field} placeholder="Jane Alvarez" data-testid="rebate-form-name" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="rb-email" className={label}>Work email *</label>
          <input id="rb-email" name="contact_email" type="email" required value={form.contact_email} onChange={onChange}
            className={field} placeholder="jalvarez@hospital.org" data-testid="rebate-form-email" />
        </div>
        <div>
          <label htmlFor="rb-phone" className={label}>Phone</label>
          <input id="rb-phone" name="contact_phone" value={form.contact_phone} onChange={onChange}
            className={field} placeholder="(281) 555-0134" data-testid="rebate-form-phone" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="rb-spend" className={label}>Estimated combined quarterly spend</label>
          <input id="rb-spend" name="estimated_quarterly_spend" type="number" min="0" step="1000"
            value={form.estimated_quarterly_spend} onChange={onChange}
            className={field} placeholder="120000" data-testid="rebate-form-spend" />
        </div>
        <div>
          <label htmlFor="rb-payout" className={label}>Preferred payout</label>
          <select id="rb-payout" name="payout_preference" value={form.payout_preference} onChange={onChange}
            className={field} data-testid="rebate-form-payout">
            <option value="credit">Credit against future orders</option>
            <option value="ach">ACH or check</option>
          </select>
        </div>
      </div>

      <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 cursor-pointer hover:border-cyan-400 transition-colors">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
          className="mt-0.5 w-4 h-4 shrink-0 accent-cyan-600" data-testid="rebate-form-ack" />
        <span className="text-xs text-slate-600 leading-relaxed">
          {program?.safe_harbor_notice}
          <strong className="block mt-2 text-slate-800">
            I acknowledge this notice on behalf of the facility.
          </strong>
        </span>
      </label>

      {status === "error" && (
        <p className="text-sm text-rose-600" data-testid="rebate-form-error">{msg}</p>
      )}
      <button type="submit" disabled={status === "sending"} data-testid="rebate-form-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-[#0056A8] hover:bg-[#0068c8] disabled:opacity-60 text-white font-black text-sm transition-colors shadow-lg shadow-[#0056A8]/25">
        {status === "sending"
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
          : <>Enroll in the rebate program <ArrowRight className="w-4 h-4" /></>}
      </button>
      <p className="text-[11px] text-slate-500 text-center">
        No GPO contract required. No minimum commitment. Cancel at any time.
      </p>
    </form>
  );
}

export default function RebateProgramPage() {
  const [program, setProgram] = useState(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    fetch(`${API}/api/rebates/program`).then((r) => r.json()).then(setProgram).catch(() => {});
  }, []);

  const tiers = program?.tiers || [];
  const lines = program?.contracted_lines || [];
  const topTier = tiers.length ? tiers[tiers.length - 1] : null;

  return (
    <div className="min-h-screen bg-white" data-testid="rebate-program-page">
      <SEO
        title="Hospital Rebate Program — Quarterly Volume Rebates on Contracted Lines & Backorders | Sterile Mate"
        description="A GPO-style volume rebate for hospitals. Up to 3.0% on contracted lines — Filmop, Sol Millennium, American Surgical, Brewer, Vive Health, Sterile Mate private-label nitrile gloves and TechInquest software — plus a rebate on sourced backorders. Retroactive quarterly tiers, monthly statements, no GPO contract required. Launching January 2027."
        canonical="/solutions/rebate-program"
        keywords={[
          "hospital volume rebate program", "GPO alternative rebate hospital distributor",
          "contracted line rebate healthcare supply chain", "hospital backorder rebate program",
          "quarterly volume rebate healthcare", "nitrile glove rebate program hospital",
          "supply chain rebate statement hospital", "non-GPO rebate program",
        ]}
      />
      <PublicSiteHeader />

      {/* ───────────────────────── HERO ───────────────────────── */}
      <section className="relative overflow-hidden bg-[#050a16]">
        <img src={IMG.boardroom} alt="Hospital supply chain leadership in a boardroom strategy session"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050a16] via-[#050a16]/92 to-[#0b1e3f]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050a16] via-transparent to-[#050a16]/40" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <motion.div
            initial={reduce ? false : "hide"} animate={reduce ? false : "show"}
            variants={{ show: { transition: { staggerChildren: 0.09 } } }}
            className="max-w-3xl"
          >
            {[
              <div key="pill" className="inline-flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full bg-amber-500/15 border border-amber-400/40 backdrop-blur-sm"
                data-testid="rebate-launch-badge">
                <span className="px-2 py-0.5 rounded-full bg-amber-500 text-[10px] font-black uppercase tracking-wider text-slate-900">
                  Launching
                </span>
                <span className="text-xs font-bold text-amber-100">
                  Program begins January 1, 2027 · enrollment open now
                </span>
              </div>,
              <p key="eyebrow" className="text-[11px] font-black uppercase tracking-[0.3em] text-cyan-300 mt-7"
                data-testid="rebate-hero-eyebrow">
                {program?.program_name || "Secondary Source Rebate Program"}
              </p>,
              <h1 key="h1" className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mt-5"
                data-testid="rebate-hero-headline">
                Your everyday lines.<br />
                Your hardest backorders.<br />
                <span className="text-cyan-400">One rebate that pays on both.</span>
              </h1>,
              <p key="lead" className="text-lg text-slate-200 leading-relaxed mt-7 max-w-2xl">
                Consolidate the product lines you already buy every week — and the items nobody else
                can fill — with a single secondary source. Your combined quarterly spend sets the
                tier, and the tier pays retroactively on the first dollar of the quarter.
              </p>,
              <div key="cta" className="flex flex-wrap items-center gap-4 mt-9">
                <a href="#tiers" data-testid="rebate-hero-cta-tiers"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-[#0056A8] hover:bg-[#0068c8] text-white font-black text-sm transition-colors shadow-lg shadow-[#0056A8]/30">
                  See the tier table <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#enroll" data-testid="rebate-hero-cta-enroll"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-xl border border-white/25 bg-white/5 hover:bg-white/10 hover:border-cyan-400/60 text-white text-sm font-bold backdrop-blur-sm transition-colors">
                  Enroll ahead of launch
                </a>
              </div>,
            ].map((child) => (
              <motion.div key={child.key}
                variants={{ hide: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
                {child}
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* stat rail */}
        <div className="relative border-t border-white/10 bg-[#050a16]/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {[
              { k: "rate", v: pct(topTier?.contract_rate_pct ?? 2), l: "Top tier, contracted lines", s: "Retroactive to dollar one" },
              { k: "entry", v: money0(program?.contract_minimum ?? 100000), l: "Contracted rebate begins", s: "Combined quarterly spend" },
              { k: "payout", v: `${program?.payout_days_after_quarter_close ?? 45} days`, l: "After quarter close", s: "Order credit or ACH" },
            ].map((s) => (
              <div key={s.k} className="py-7 sm:px-8 first:sm:pl-0" data-testid={`rebate-hero-stat-${s.k}`}>
                <p className="text-3xl lg:text-4xl font-black text-white tabular-nums tracking-tight">{s.v}</p>
                <p className="text-xs font-bold uppercase tracking-wider text-cyan-300 mt-1.5">{s.l}</p>
                <p className="text-xs text-slate-400 mt-1">{s.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── GPO COMPARISON ───────────────── */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal className="max-w-2xl mb-12">
            <Overline>How this compares</Overline>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-5">
              Measured against what a GPO actually returns to you.
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Group purchasing administrative fees are capped at 3% of the purchase price under
              the GPO safe harbor at 42 C.F.R. § 1001.952(j), are commonly quoted in the 1–3%
              range, and only a share of that reaches the member. Published supplier volume
              rebates sit at roughly 2% of net purchases, settled annually and applied to the
              marginal spend. That is the benchmark this program is built against — not a
              competing claim, just arithmetic.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6" data-testid="rebate-gpo-comparison-card">
            <Reveal>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 mb-5">
                  Typical GPO share-back
                </p>
                <p className="text-5xl font-black text-slate-400 tabular-nums tracking-tight">1–3%</p>
                <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                  administrative fee, capped at 3% of the purchase price under the GPO safe
                  harbor. What reaches the member is a share of that, and a typical supplier
                  volume rebate runs around 2% of net purchases.
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-slate-500">
                  <li className="flex gap-2.5"><span className="text-slate-300 mt-0.5">—</span> Requires a signed letter of commitment</li>
                  <li className="flex gap-2.5"><span className="text-slate-300 mt-0.5">—</span> Increases usually apply only to the marginal spend</li>
                  <li className="flex gap-2.5"><span className="text-slate-300 mt-0.5">—</span> Often settled annually, and backorders sourced off contract rarely qualify</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="relative h-full rounded-2xl border-2 border-[#0056A8] bg-gradient-to-br from-[#050a16] to-[#0b1e3f] p-8 shadow-2xl shadow-[#0b1e3f]/25 overflow-hidden">
                <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl" />
                <p className="relative text-[11px] font-black uppercase tracking-[0.2em] text-cyan-300 mb-5">
                  Sterile Mate, top tier
                </p>
                <p className="relative text-5xl font-black text-white tabular-nums tracking-tight">
                  {pct(topTier?.contract_rate_pct ?? 2)}
                </p>
                <p className="relative text-sm text-slate-300 mt-3 leading-relaxed">
                  on contracted lines from {money0(program?.contract_minimum ?? 100000)} of
                  combined quarterly spend, plus {pct(topTier?.backorder_rate_pct ?? 2)} on
                  sourced backorders — paid quarterly, and retroactive to the first dollar
                  rather than only on the spend above the threshold.
                </p>
                <ul className="relative mt-6 space-y-2.5 text-sm text-slate-200">
                  {[
                    "No GPO contract and no membership fee",
                    "Retroactive to dollar one, not marginal",
                    "Paid quarterly, not once a year",
                  ].map((t) => (
                    <li key={t} className="flex gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────── TWO TRACKS ───────────────── */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <Reveal>
              <Overline>Two tracks, one ladder</Overline>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-6">
                We can tell you exactly what the rebate is on the lines you buy every day.
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-5">
                On the lines we distribute under agreed contract pricing, the rebate rate is
                published firm and your team can model it before the order is placed. A backorder
                is sourced against allocation anywhere in North America, where neither
                availability nor price can be promised in advance — so that track carries its own,
                separately published rate.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                <strong className="text-slate-900">Both count toward the same tier.</strong> Your
                combined quarterly spend decides which tier you are in, and each track then pays at
                its own rate — so the routine volume you consolidate with us lifts what your
                hardest backorders earn. The contracted-line rebate opens at{" "}
                {money0(program?.contract_minimum ?? 100000)} of combined quarterly spend.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {(program?.tracks || []).map((t, i) => (
                  <div key={t.key}
                    className={`rounded-xl border p-5 ${i === 0 ? "border-[#0056A8]/30 bg-sky-50/60" : "border-slate-200 bg-slate-50"}`}
                    data-testid={`rebate-track-${t.key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {i === 0 ? <Boxes className="w-4 h-4 text-[#0056A8]" /> : <Package className="w-4 h-4 text-slate-500" />}
                      <p className="text-sm font-black text-slate-900">{t.label}</p>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{t.note}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
                <img src={IMG.warehouse} alt="Organized medical distribution facility inventory"
                  className="w-full h-[420px] lg:h-[520px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050a16] via-[#050a16]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <p className="text-[11px] font-black uppercase tracking-[0.2em] text-cyan-300 mb-2">
                    Sourced anywhere in North America
                  </p>
                  <p className="text-white text-base font-semibold leading-snug">
                    Six years of secondary-source sourcing, now paying you back on the volume you
                    were already placing.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────── TIER TABLE ───────────────── */}
      <section id="tiers" className="bg-slate-50 border-y border-slate-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal className="max-w-2xl mb-12">
            <Overline>The tier table</Overline>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-5">
              Combined quarterly spend sets the tier. Every tier is retroactive.
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Most volume programs pay only on the dollars above a threshold, and settle once a
              year. Ours pays the rate you reach on the entire quarter. The contracted-line
              rebate begins at {money0(program?.contract_minimum ?? 100000)} of combined
              quarterly spend — below that, your backorder volume still earns the Tier 1
              backorder rate and still counts toward reaching the threshold.
            </p>
          </Reveal>

          {/* desktop */}
          <div className="hidden md:block rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-xl shadow-slate-900/5"
            data-testid="rebate-tier-table">
            <table className="w-full">
              <thead>
                <tr className="bg-[#050a16] text-left">
                  <th className="py-4 px-6 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Tier</th>
                  <th className="py-4 px-6 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Combined quarterly spend</th>
                  <th className="py-4 px-6 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Annual run rate</th>
                  <th className="py-4 px-6 text-right text-[10px] font-black uppercase tracking-[0.18em] text-cyan-300">Contracted lines</th>
                  <th className="py-4 px-6 text-right text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Sourced backorders</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t) => (
                  <tr key={t.tier}
                    className={`border-t border-slate-200 transition-colors hover:bg-sky-50/60 ${t.tier === tiers.length ? "bg-sky-50" : "bg-white"}`}
                    data-testid={`rebate-tier-row-${t.tier}`}>
                    <td className="py-5 px-6">
                      <span className="font-black text-slate-900">{t.label}</span>
                      {t.tier === tiers.length && (
                        <span className="ml-2 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-[#0056A8] text-white">
                          Top
                        </span>
                      )}
                    </td>
                    <td className="py-5 px-6 text-slate-700 tabular-nums text-sm">
                      {money0(t.min)}{t.max ? ` – ${money0(t.max)}` : " and above"}
                    </td>
                    <td className="py-5 px-6 text-slate-500 tabular-nums text-sm">{money0(t.annual_run_rate)}</td>
                    <td className="py-5 px-6 text-right font-black text-[#0056A8] tabular-nums text-lg">
                      {t.contract_rate_pct ? pct(t.contract_rate_pct) : (
                        <span className="text-xs font-bold text-slate-400">
                          from {money0(program?.contract_minimum ?? 100000)}
                        </span>
                      )}
                    </td>
                    <td className="py-5 px-6 text-right font-bold text-slate-600 tabular-nums text-lg">{pct(t.backorder_rate_pct)}</td>
                  </tr>
                ))}
                {!tiers.length && (
                  <tr><td colSpan={5} className="py-10 px-6 text-slate-400 text-sm">Loading tier table…</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* mobile */}
          <div className="md:hidden space-y-4">
            {tiers.map((t) => (
              <div key={t.tier}
                className={`rounded-2xl border p-5 ${t.tier === tiers.length ? "border-[#0056A8] bg-white shadow-lg shadow-[#0056A8]/10" : "border-slate-200 bg-white"}`}
                data-testid={`rebate-tier-card-${t.tier}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-black text-slate-900">{t.label}</p>
                  {t.tier === tiers.length && (
                    <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-[#0056A8] text-white">Top</span>
                  )}
                </div>
                <p className="text-sm text-slate-700 tabular-nums">
                  {money0(t.min)}{t.max ? ` – ${money0(t.max)}` : " and above"}
                </p>
                <p className="text-xs text-slate-500 tabular-nums mt-0.5">{money0(t.annual_run_rate)} a year</p>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="rounded-lg bg-sky-50 border border-[#0056A8]/20 px-3 py-2.5">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#0056A8]">Contracted</p>
                    <p className="text-xl font-black text-[#0056A8] tabular-nums">
                      {t.contract_rate_pct ? pct(t.contract_rate_pct) : (
                        <span className="text-[11px] font-bold text-slate-400">
                          from {money0(program?.contract_minimum ?? 100000)}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Backorders</p>
                    <p className="text-xl font-black text-slate-700 tabular-nums">{pct(t.backorder_rate_pct)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-6 lg:p-7 flex flex-col sm:flex-row gap-5 sm:items-center"
              data-testid="rebate-retroactive-callout">
              <Repeat className="w-8 h-8 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-900 leading-relaxed">
                <strong>Retroactive to dollar one.</strong>{" "}
                {program?.retroactive_note
                  || "The tier you reach applies to the first dollar of the quarter, not just the dollars above the threshold."}{" "}
                A hospital that lands at {money0(tiers[2]?.min ?? 175000)} earns the Tier 3 rates on
                all of it — not just on the dollars past the line.
              </p>
            </div>
          </Reveal>

          {/* kickers */}
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              {
                k: "growth", icon: TrendingUp,
                title: `+${program?.growth?.rate_pct ?? 0.5}% growth`,
                body: `Earned on the whole quarter when your combined qualified spend is at least ${program?.growth?.threshold_pct ?? 15}% above the same quarter last year.`,
              },
              {
                k: "commitment", icon: Target,
                title: `+${program?.commitment?.rate_pct ?? 0.5}% commitment`,
                body: `Earned when at least ${program?.commitment?.target_pct ?? 80}% of your contracted-line and backorder volume is routed to Sterile Mate first.`,
              },
              {
                k: "annual", icon: Award,
                title: `+${program?.annual_bonus?.rate_pct ?? 0.5}% annual bonus`,
                body: `Added on the full year once annual qualified spend crosses ${money0(program?.annual_bonus?.threshold ?? 1000000)}, on top of every quarterly tier already earned.`,
              },
            ].map((c, i) => (
              <Reveal key={c.k} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0056A8]/40 hover:shadow-lg hover:shadow-slate-900/5 transition-all"
                  data-testid={`rebate-kicker-${c.k}`}>
                  <c.icon className="w-5 h-5 text-[#0056A8] mb-4" />
                  <p className="text-lg font-black text-slate-900 mb-2">{c.title}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-8 text-sm text-slate-500 max-w-3xl leading-relaxed">
              Stacked at the top tier with growth, commitment and the annual bonus, the blended rate
              reaches <strong className="text-slate-900">{program?.max_blended_rate_pct ?? "4.5"}%</strong> of
              qualified spend. Rebates are paid within {program?.payout_days_after_quarter_close ?? 45} days
              of quarter close as a credit against future orders or by ACH — your choice at enrollment.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────────────── CONTRACT PRICING ───────────────── */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Overline>Contract pricing</Overline>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
                {program?.contract_pricing?.headline
                  || "Your contract pricing is set with you, not handed to you."}
              </h2>
            </Reveal>
            <div className="lg:col-span-7 space-y-5" data-testid="rebate-contract-pricing">
              {(program?.contract_pricing?.points || []).map((p, i) => (
                <Reveal key={p.slice(0, 24)} delay={i * 0.08}>
                  <div className="flex gap-4 rounded-xl border border-slate-200 bg-slate-50 p-5"
                    data-testid={`rebate-pricing-point-${i}`}>
                    <CheckCircle2 className="w-5 h-5 text-[#0056A8] shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700 leading-relaxed">{p}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── CONTRACTED LINES ───────────────── */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal className="max-w-2xl mb-12">
            <Overline>Contracted lines</Overline>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-5">
              The lines we are a select distributor for.
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Everything below earns the published contracted rate for your tier. These are the
              predetermined lines you use every day — which is exactly why we can quote the rebate
              before you place the order.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rebate-contracted-lines">
            {lines.map((l, i) => {
              const Icon = LINE_ICONS[l.name] || Package;
              return (
                <Reveal key={l.name} delay={Math.min(i * 0.06, 0.3)}>
                  <a href={l.url || "/marketplace"}
                    className="block h-full rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0056A8]/40 hover:shadow-xl hover:shadow-slate-900/5 transition-all group"
                    data-testid={`rebate-line-${i}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#050a16] flex items-center justify-center mb-4 group-hover:bg-[#0056A8] transition-colors">
                      <Icon className="w-5 h-5 text-cyan-300" />
                    </div>
                    <p className="text-base font-black text-slate-900 mb-1.5">{l.name}</p>
                    <p className="text-sm text-slate-600 leading-relaxed">{l.category}</p>
                    <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#0056A8] group-hover:gap-2.5 transition-all">
                      Browse the catalog <ArrowRight className="w-3.5 h-3.5" />
                    </p>
                  </a>
                </Reveal>
              );
            })}
            {!lines.length && (
              <p className="text-sm text-slate-400">Loading contracted lines…</p>
            )}
          </div>
        </div>
      </section>

      {/* ───────────────── HOW IT WORKS ───────────────── */}
      <section className="relative bg-[#050a16]">
        <img src={IMG.hospital} alt="Modern hospital campus"
          className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050a16] via-[#050a16]/92 to-[#050a16]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-cyan-300 mb-4">How it works</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-5">
              Four steps, and your finance team can audit every one of them.
            </h2>
            <p className="text-base text-slate-300 leading-relaxed">
              Nothing about this program requires you to take our word for the arithmetic.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            {[
              { n: "01", t: "Enroll on one page", d: "Name, contact and payout preference, plus the written safe-harbor acknowledgement your cost-reporting obligation requires. No GPO contract, no membership fee, no minimum commitment." },
              { n: "02", t: "A statement arrives monthly", d: "Starting February 3, 2027, on the 3rd of each month you get spend to date by track, your tier, the exact calculation, every qualified PO line and the gap to the next threshold while the quarter is still open." },
              { n: "03", t: "The quarter closes and is reconciled", d: `The first rebate quarter closes March 31, 2027. Payment is issued within ${program?.payout_days_after_quarter_close ?? 45} days of quarter close, as a credit against future orders or by ACH — your choice at enrollment.` },
              { n: "04", t: "Your portal shows it live", d: "Between statements, the rebate dashboard in your Sterile Mate portal shows current position, accrual by track and the gap to the next tier in real time." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="flex gap-5" data-testid={`rebate-step-${s.n}`}>
                  <p className="text-2xl font-black text-cyan-400/60 tabular-nums shrink-0">{s.n}</p>
                  <div>
                    <p className="text-lg font-bold text-white mb-2">{s.t}</p>
                    <p className="text-sm text-slate-300 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── ENROLL ───────────────── */}
      <section id="enroll" className="bg-slate-50 border-b border-slate-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
            <Reveal className="lg:col-span-2">
              <Overline>Enroll</Overline>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-5">
                Enroll before January and your first quarter counts from day one.
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {program?.launch?.note
                  || "Enrollment is open now. Qualified spend starts counting January 1, 2027 and the first rebate quarter closes March 31, 2027."}
              </p>

              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-900/5 mb-8">
                <img src={IMG.leaders} alt="Diverse hospital supply chain leadership team in discussion"
                  className="w-full h-56 object-cover" />
              </div>

              <ul className="space-y-3">
                {[
                  { icon: CalendarClock, t: `Accrual begins ${program?.launch?.date || "2027-01-01"}` },
                  { icon: FileText, t: "First monthly statement February 3, 2027" },
                  { icon: ShieldCheck, t: "Written safe-harbor terms, fixed at the time of sale" },
                ].map((r) => (
                  <li key={r.t} className="flex items-center gap-3 text-sm text-slate-700">
                    <r.icon className="w-4 h-4 text-[#0056A8] shrink-0" /> {r.t}
                  </li>
                ))}
              </ul>
            </Reveal>

            <div className="lg:col-span-3 w-full">
              <Reveal delay={0.1}>
                <EnrollForm program={program} />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── COMPLIANCE ───────────────── */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <Reveal>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 lg:p-10"
              data-testid="rebate-safe-harbor-footer">
              <div className="flex items-center gap-3 mb-5">
                <ShieldCheck className="w-5 h-5 text-[#0056A8]" />
                <p className="text-sm font-black text-slate-900 uppercase tracking-wider">
                  Discount safe harbor · 42 C.F.R. § 1001.952(h)
                </p>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">
                {program?.safe_harbor_notice}
              </p>
              <p className="text-xs text-slate-500 leading-relaxed">
                {program?.invoice_disclosure_line}
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
